/**
 * Shows the notification that is waiting, in the centre of the screen, until the
 * user clicks "Noted".
 *
 * Desk, 2026-09-27: "any notification coming should be seen at the centre of the
 * page to understand by the user what it is… give a Noted button… elaborated,
 * understandable way." Mounted once, in `AppProviders`, so it covers every
 * screen — including login.
 *
 * WHAT MAKES IT UNDERSTANDABLE. A plain heading that says what KIND of news it is
 * ("This did not go through" rather than "Error"), the full message at reading
 * size, a "What you can do" line, and the time — the one thing a user can quote
 * when they report it.
 *
 * NOT DISMISSIBLE BY ACCIDENT. No Escape, no backdrop click, no corner X: only
 * "Noted" closes it, because a notification that can be brushed away is the toast
 * again. To stop a keystroke from a form the user was typing in (an Enter that
 * lands as the dialog opens) from acknowledging a message nobody has read, clicks
 * in the first half-second are ignored.
 *
 * It rides the native `<dialog>` top layer via `ui-tw`'s Dialog, so it sits above
 * any form dialog that was already open and the screen behind it is inert.
 */
import { useEffect, useRef, type JSX } from 'react';
import dayjs from 'dayjs';
import { Button, Dialog, Icon, type MaterialSymbolName } from '@components/ui-tw';
import { useNotificationAckStore } from '@store/notification-ack.store';
import type { NotificationSeverity } from '@store/notification.store';
import { cn } from '@utils/cn';

/** A click this soon after the message appears cannot have read it. */
export const NOTED_GUARD_MS = 500;

const KIND: Record<NotificationSeverity, { heading: string; icon: MaterialSymbolName; tone: string; defaultStep: string }> = {
  success: {
    heading: 'Done',
    icon: 'check_circle',
    tone: 'tw:bg-nx-green-tint tw:text-nx-green-on-tint',
    defaultStep: 'This has been done. You do not need to do anything more.',
  },
  error: {
    heading: 'This did not go through',
    icon: 'error',
    tone: 'tw:bg-nx-red-tint tw:text-nx-red-on-tint',
    defaultStep: 'Check what you entered and try again. If the same message comes back, tell your supervisor and mention the time shown below.',
  },
  warning: {
    heading: 'Please check',
    icon: 'warning',
    tone: 'tw:bg-nx-yellow-tint tw:text-nx-yellow-on-tint',
    defaultStep: 'Nothing has been lost, but please read this and look at the screen before you carry on.',
  },
  info: {
    heading: 'For your information',
    icon: 'info',
    tone: 'tw:bg-nx-blue-tint tw:text-nx-blue-on-tint',
    defaultStep: 'This is for your information. No action is needed.',
  },
};

export function NotificationDialog(): JSX.Element {
  const queue = useNotificationAckStore((s) => s.queue);
  const acknowledge = useNotificationAckStore((s) => s.acknowledge);
  const acknowledgeAll = useNotificationAckStore((s) => s.acknowledgeAll);
  const current = queue[0];
  const shownAt = useRef(0);

  useEffect(() => {
    if (current) shownAt.current = Date.now();
  }, [current?.id]);

  const guarded = (action: () => void) => () => {
    if (Date.now() - shownAt.current < NOTED_GUARD_MS) return;
    action();
  };

  const kind = current ? KIND[current.severity] : KIND.info;
  const waiting = queue.length - 1;

  return (
    <Dialog
      open={Boolean(current)}
      onClose={() => undefined}
      dismissible={false}
      size="md"
      title={current?.title ?? ''}
      footer={
        current && (
          <>
            {waiting > 0 && (
              <>
                <span className="tw:mr-auto tw:text-label tw:text-nx-text-muted">
                  1 of {queue.length} — {waiting} more waiting
                </span>
                <Button variant="outline" onClick={guarded(acknowledgeAll)}>
                  Noted all ({queue.length})
                </Button>
              </>
            )}
            <Button autoFocus onClick={guarded(acknowledge)}>
              Noted
            </Button>
          </>
        )
      }
    >
      {current && (
        <div role="alert" className="tw:flex tw:flex-col tw:gap-3">
          <div className="tw:flex tw:items-start tw:gap-3">
            <span className={cn('tw:flex tw:size-10 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full', kind.tone)}>
              <Icon name={kind.icon} size={24} />
            </span>
            <div className="tw:min-w-0">
              <p className="tw:m-0 tw:text-label tw:font-semibold tw:uppercase tw:tracking-wide tw:text-nx-text-muted">{kind.heading}</p>
              {current.message ? (
                <p className="tw:m-0 tw:mt-1 tw:whitespace-pre-wrap tw:break-words tw:text-base tw:text-nx-text">{current.message}</p>
              ) : (
                <p className="tw:m-0 tw:mt-1 tw:text-base tw:text-nx-text">{current.title}</p>
              )}
            </div>
          </div>

          <div className={cn('tw:rounded-md tw:px-3 tw:py-2 tw:text-sm', kind.tone)}>
            <b>What you can do: </b>
            {current.nextStep ?? kind.defaultStep}
          </div>

          <p className="tw:m-0 tw:text-label tw:text-nx-text-muted">
            Shown at {dayjs(current.at).format('DD/MM/YYYY HH:mm:ss')}
            {current.count > 1 ? ` · happened ${current.count} times` : ''}
          </p>
        </div>
      )}
    </Dialog>
  );
}
